import dayjs from "dayjs";
import { useFetchTenantConfigurationQuery } from "queries/configuration.queries";
import { RsvpEventDetails, RsvpStatusDetails } from "queries/rsvp.queries";
import React, { useEffect, useState } from "react";
import { Text, View } from "react-native";
import { RsvpTone, useRsvpColors } from "../theme";
import RsvpPrimaryButton from "./RsvpPrimaryButton";
import RsvpQuantityStepper from "./RsvpQuantityStepper";
import RsvpStatusIcon, { RsvpIconName } from "./RsvpStatusIcon";

interface Props {
  details: RsvpStatusDetails;
  event?: RsvpEventDetails;
  // Max seats for the reserved event (from the events list); null means no cap.
  maxQuantity?: number | null;
  // Shown as a "Player ID" row so the player can verify who the RSVP is for.
  playerId?: string;
  // Hide the Event/Date/Venue block (e.g. when a reserved event list is shown
  // above instead). Defaults to true.
  showDetails?: boolean;
  onCancel: () => void;
  isCancelling?: boolean;
  // When provided, pending/waitlisted RSVPs get an inline quantity editor.
  onUpdateQuantity?: (quantity: number) => void;
  isUpdatingQuantity?: boolean;
  // When provided, an OFFERED reservation gets an Accept Offer button.
  onAcceptOffer?: () => void;
  isAccepting?: boolean;
}

interface StatusMeta {
  icon: RsvpIconName;
  tone: RsvpTone;
  title: string;
  desc?: string;
}

const metaForStatus = (details: RsvpStatusDetails): StatusMeta => {
  switch (details.status) {
    case "WAITLISTED":
      return {
        icon: "clock",
        tone: "warning",
        title: "You're on the waitlist",
        desc: "You'll be notified if a spot becomes available.",
      };
    case "OFFERED":
      return {
        icon: "bell",
        tone: "info",
        title: "A spot opened up!",
        desc: "Confirm you still want to attend, or decline to release the spot to the next player. If you don't respond before the deadline, the spot moves to the next player and you stay on the waitlist.",
      };
    // Approved reservations hold real tickets, so quantity can no longer be
    // edited here — that requires cancelling (which releases the tickets)
    // and re-RSVPing.
    case "CONFIRMED":
      return {
        icon: "check",
        tone: "success",
        title: "You're confirmed",
        desc: details.cancellationCutoffPassed
          ? "You can only hold one RSVP for this event."
          : "You can only hold one RSVP for this event. To change your reservation or if you can no longer attend, please cancel your RSVP below.",
      };
    // Awaiting-review (PENDING) reservations are presented to the fan as
    // confirmed too, but their quantity can still be edited in place.
    case "PENDING":
    default:
      return {
        icon: "check",
        tone: "success",
        title: "You're confirmed",
        desc: details.cancellationCutoffPassed
          ? "You can only hold one RSVP for this event."
          : "You can only hold one RSVP for this event. If you can no longer attend, please cancel your RSVP below.",
      };
  }
};

const CardRow: React.FC<{ label: string; value: string; last?: boolean }> = ({
  label,
  value,
  last,
}) => {
  const colors = useRsvpColors();
  return (
    <View style={{ marginBottom: last ? 0 : 12 }}>
      <Text style={{ color: colors.textMuted, fontSize: 13, marginBottom: 2 }}>
        {label}
      </Text>
      <Text
        style={{ color: colors.textPrimary, fontSize: 16, fontWeight: "500" }}
      >
        {value}
      </Text>
    </View>
  );
};

const RsvpStatusCard: React.FC<Props> = ({
  details,
  event,
  maxQuantity = null,
  playerId,
  showDetails = true,
  onCancel,
  isCancelling,
  onUpdateQuantity,
  isUpdatingQuantity,
  onAcceptOffer,
  isAccepting,
}) => {
  const meta = metaForStatus(details);
  const colors = useRsvpColors();
  const { data: tenantConfiguration } = useFetchTenantConfigurationQuery();

  // Only pending/waitlisted RSVPs may change quantity (approved reservations
  // hold real tickets), and only before the event's cancellation cutoff;
  // mirrors the backend rules.
  const canEditQuantity =
    !!onUpdateQuantity &&
    details.quantity != null &&
    !details.cancellationCutoffPassed &&
    (details.status === "PENDING" || details.status === "WAITLISTED");

  // The quantity editor stays collapsed behind a "Modify RSVP" button
  const [editingQuantity, setEditingQuantity] = useState(false);
  const [quantity, setQuantity] = useState(details.quantity ?? 1);
  useEffect(() => {
    setQuantity(details.quantity ?? 1);
    // A saved quantity change closes the editor
    setEditingQuantity(false);
  }, [details.quantity]);
  // Closing the editor without saving discards any unsaved quantity change and
  // returns to the standard confirmed view.
  const closeEditor = () => {
    setQuantity(details.quantity ?? 1);
    setEditingQuantity(false);
  };
  const quantityChanged = quantity !== details.quantity;

  const eventDate = event
    ? dayjs(event.eventDate)
        .tz(tenantConfiguration?.timezone)
        .format("dddd, MMMM D, YYYY · h:mm A")
    : null;

  const isOffered = details.status === "OFFERED";
  const offerDeadline =
    isOffered && details.offerExpiresAt
      ? dayjs(details.offerExpiresAt)
          .tz(tenantConfiguration?.timezone)
          .format("dddd, MMMM D, YYYY · h:mm A")
      : null;

  const showSeatsRow = details.quantity != null;
  const showPlayerRow = !!playerId;

  return (
    <View>
      <View style={{ alignItems: "center", marginBottom: 24 }}>
        <View style={{ marginBottom: 12 }}>
          <RsvpStatusIcon name={meta.icon} tone={meta.tone} />
        </View>
        <Text
          style={{
            fontSize: 24,
            fontWeight: "700",
            textAlign: "center",
            color: colors.textPrimary,
          }}
        >
          {meta.title}
        </Text>
        {meta.desc ? (
          <Text
            style={{
              color: colors.textMuted,
              fontSize: 15,
              textAlign: "center",
              marginTop: 8,
            }}
          >
            {meta.desc}
          </Text>
        ) : null}
      </View>

      {showDetails ? (
        <View
          style={{
            backgroundColor: colors.cardBg,
            borderWidth: 1,
            borderColor: colors.cardBorder,
            borderRadius: 16,
            padding: 20,
            marginBottom: 24,
          }}
        >
          <CardRow
            label="Event"
            value={details.eventName ?? ""}
            last={!eventDate && !event && !showPlayerRow && !showSeatsRow}
          />
          {eventDate ? (
            <CardRow
              label="Date"
              value={eventDate}
              last={!event && !showPlayerRow && !showSeatsRow}
            />
          ) : null}
          {event ? (
            <CardRow
              label="Venue"
              value={event.venueName}
              last={!showPlayerRow && !showSeatsRow}
            />
          ) : null}
          {showPlayerRow ? (
            <CardRow label="Player ID" value={playerId!} last={!showSeatsRow} />
          ) : null}
          {showSeatsRow ? (
            <CardRow label="Seats" value={String(details.quantity)} last />
          ) : null}
        </View>
      ) : null}

      {!showDetails && (showSeatsRow || showPlayerRow) ? (
        <View
          style={{
            backgroundColor: colors.cardBg,
            borderWidth: 1,
            borderColor: colors.cardBorder,
            borderRadius: 16,
            padding: 20,
            marginBottom: 24,
          }}
        >
          {showPlayerRow ? (
            <CardRow label="Player ID" value={playerId!} last={!showSeatsRow} />
          ) : null}
          {showSeatsRow ? (
            <CardRow label="Seats" value={String(details.quantity)} last />
          ) : null}
        </View>
      ) : null}

      {canEditQuantity && !editingQuantity ? (
        <RsvpPrimaryButton
          title="Modify RSVP"
          onPress={() => setEditingQuantity(true)}
          style={{ marginBottom: 24 }}
          testID="modify-rsvp"
        />
      ) : null}

      {canEditQuantity && editingQuantity ? (
        <View
          style={{
            backgroundColor: colors.cardBg,
            borderWidth: 1,
            borderColor: colors.cardBorder,
            borderRadius: 16,
            padding: 20,
            marginBottom: 24,
            alignItems: "center",
          }}
        >
          <Text
            style={{
              color: colors.textMuted,
              fontSize: 14,
              fontWeight: "500",
              marginBottom: 12,
            }}
          >
            Seats
          </Text>
          <RsvpQuantityStepper
            quantity={quantity}
            onChange={setQuantity}
            maxQuantity={maxQuantity}
          />
          {/* Greyed out until the quantity differs from the saved RSVP */}
          <View style={{ marginTop: 16, alignSelf: "stretch" }}>
            <RsvpPrimaryButton
              title="Update"
              loading={isUpdatingQuantity}
              disabled={!quantityChanged}
              onPress={() => onUpdateQuantity?.(quantity)}
              testID="update-rsvp-quantity"
            />
          </View>
        </View>
      ) : null}

      {isOffered && onAcceptOffer ? (
        <View style={{ marginBottom: 12 }}>
          <RsvpPrimaryButton
            title="Accept Spot"
            onPress={onAcceptOffer}
            loading={isAccepting}
            testID="accept-offer"
          />
          {offerDeadline ? (
            <Text
              style={{
                color: colors.textFaint,
                fontSize: 12,
                textAlign: "center",
                marginTop: 8,
              }}
            >
              Respond by {offerDeadline}.
            </Text>
          ) : null}
        </View>
      ) : null}

      {details.cancellationCutoffPassed ? (
        <Text
          style={{
            color: colors.textFaint,
            fontSize: 13,
            textAlign: "center",
            paddingVertical: 12,
          }}
        >
          Cancellation is closed for this event.
        </Text>
      ) : (
        <View>
          {canEditQuantity && editingQuantity ? (
            // While modifying, the cancel slot becomes Close, which just leaves
            // the editor; cancelling the RSVP is only offered from the standard view.
            <RsvpPrimaryButton
              title="Close"
              onPress={closeEditor}
              disabled={isUpdatingQuantity}
              style={{ marginBottom: 12 }}
              testID="close-rsvp-editor"
            />
          ) : (
            <RsvpPrimaryButton
              title={isOffered ? "Decline Offer" : "Cancel RSVP"}
              onPress={onCancel}
              loading={isCancelling}
              style={{ marginBottom: 12 }}
              testID="cancel-rsvp"
            />
          )}
          {details.cancellationCutoffHours != null ? (
            <Text
              style={{
                color: colors.textFaint,
                fontSize: 12,
                textAlign: "center",
              }}
            >
              Cancellations and changes close{" "}
              {details.cancellationCutoffHours === 1
                ? "1 hour"
                : `${details.cancellationCutoffHours} hours`}{" "}
              before the event.
            </Text>
          ) : null}
        </View>
      )}
    </View>
  );
};

export default RsvpStatusCard;
